import { Badge, Box, Card, Flex, Grid, Heading, ProviderIcon, Text } from '@workos-inc/design-system';
import { getMatter } from '@workos-inc/docs-kit';
import { glob } from 'glob';
import Head from 'next/head';
import NextLink from 'next/link';
import { GetStaticProps } from 'next/types';
import path from 'path';
import * as React from 'react';
import { Header } from 'src/components/header';
import { NavigationWrapper } from 'src/components/navigation-wrapper';
import { PageBackground } from 'src/components/page-background';
import { PageLayout } from 'src/components/page-layout';
import { Subtitle } from 'src/components/subtitle';
import { CONTENT_DIR } from 'src/utils/constants';
import { createPageImage, createPageTitle } from 'src/utils/create-page-metadata';
interface Integration {
  url: string;
  title: string;
  description: string;
  icon?: React.ComponentPropsWithoutRef<typeof ProviderIcon>['provider'];
  badge?: string;
}
export const getStaticProps: GetStaticProps = async () => {
  const files = await glob(`${CONTENT_DIR}/{integrations,user-management/add-ons}/[!_]*.mdx`, {
    ignore: [`${CONTENT_DIR}/integrations/auth0-enterprise-connection.mdx`, `${CONTENT_DIR}/integrations/auth0-directory-sync.mdx`]
  });
  const filesMeta = files.map<Integration>(file => {
    const data = getMatter(file);
    const url = path.relative(CONTENT_DIR, file).replace('.mdx', '');
    // Introducing lint rule banning type assertions
    // This type assertion assumes data contains a title and description
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const meta = {
      url,
      ...data
    } as Integration;
    return meta;
  });
  const genericUrls = ['/integrations/saml', '/integrations/scim', '/integrations/sftp', '/integrations/oidc'];
  const sortedMeta = filesMeta.sort((a, b) => a.title.localeCompare(b.title));
  const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');
  const allAlphabetical = alphabet.map(letter => ({
    letter,
    integrations: sortedMeta.filter(({
      title,
      url
    }) => title.toLowerCase().startsWith(letter) && !genericUrls.includes(url))
  }));
  const alphabetical = allAlphabetical.filter(({
    integrations
  }) => integrations.length > 0);
  const generic = sortedMeta.filter(({
    url
  }) => genericUrls.includes(url)).sort((a, b) => genericUrls.indexOf(a.url) - genericUrls.indexOf(b.url));
  const title = 'Integrations';
  const description = 'Guides on integrating WorkOS with third-party services.';
  const image = await createPageImage('Integrations', description);
  return {
    props: {
      alphabetical,
      generic,
      pageMeta: {
        title,
        canonicalUrl: 'https://workos.com/docs/integrations',
        description,
        image
      }
    }
  };
};
interface IntegrationsGroupedAlphabetically {
  letter: string;
  integrations: Integration[];
}
interface IntegrationsPageProps {
  generic: Integration[];
  alphabetical: IntegrationsGroupedAlphabetically[];
  pageMeta: {
    title: string;
    canonicalUrl: string;
    description: string;
    image: string;
  };
}
const IntegrationsPage = ({
  alphabetical,
  generic,
  pageMeta
}: IntegrationsPageProps) => {
  const title = createPageTitle(pageMeta.title);
  const {
    description,
    image
  } = pageMeta;
  return <Box position="relative" data-sentry-element="Box" data-sentry-component="IntegrationsPage" data-sentry-source-file="integrations.tsx">
      <Head data-sentry-element="Head" data-sentry-source-file="integrations.tsx">
        <title>{title}</title>
        <meta content={description} name="description" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content={image} itemProp="image" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content={image} name="image" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content={description} name="og:description" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content={image} property="og:image" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content={title} property="og:title" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content="website" property="og:type" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content="summary_large_image" name="twitter:card" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content={description} name="twitter:description" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content={image} name="twitter:image" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <meta content={title} name="twitter:title" data-sentry-element="meta" data-sentry-source-file="integrations.tsx" />
        <link href={pageMeta.canonicalUrl} rel="canonical" />
      </Head>

      <PageBackground data-sentry-element="PageBackground" data-sentry-source-file="integrations.tsx" />

      <Header caption="Integrations" data-sentry-element="Header" data-sentry-source-file="integrations.tsx" />

      <PageLayout.Root layout="index" data-sentry-element="PageLayout.Root" data-sentry-source-file="integrations.tsx">
        <PageLayout.Nav data-sentry-element="PageLayout.Nav" data-sentry-source-file="integrations.tsx">
          <NavigationWrapper data-sentry-element="NavigationWrapper" data-sentry-source-file="integrations.tsx" />
        </PageLayout.Nav>

        <PageLayout.Content data-sentry-element="PageLayout.Content" data-sentry-source-file="integrations.tsx">
          <Box mb={{
          initial: '5',
          sm: '6',
          lg: '7'
        }} data-sentry-element="Box" data-sentry-source-file="integrations.tsx">
            <Heading as="h1" mb="2" size={{
            initial: '7',
            sm: '8'
          }} data-sentry-element="Heading" data-sentry-source-file="integrations.tsx">
              Integrations
            </Heading>
            <Subtitle data-sentry-element="Subtitle" data-sentry-source-file="integrations.tsx">{description}</Subtitle>
          </Box>

          <Box mb="6" data-sentry-element="Box" data-sentry-source-file="integrations.tsx">
            <Grid data-algolia-exclude columns={{
            sm: '2',
            md: '3'
          }} gap="4" mb="8" data-sentry-element="Grid" data-sentry-source-file="integrations.tsx">
              {generic.map(({
              url,
              title,
              description,
              badge
            }) => <Card key={url} asChild size={{
              initial: '3',
              md: '4'
            }} variant="classic">
                  <NextLink href={url}>
                    <Flex align="center" gap="4" justify="between" my="-1">
                      <Box>
                        <Flex align="center" gap="2" mb="1">
                          <Heading as="h3" size="3" trim="start">
                            {title}
                          </Heading>
                          {badge && <Badge color="purple">{badge}</Badge>}
                        </Flex>
                        <Text as="div" color="gray" size="2" trim="end" wrap="balance">
                          {description}
                        </Text>
                      </Box>
                    </Flex>
                  </NextLink>
                </Card>)}
            </Grid>

            {alphabetical.map(group => <Box key={group.letter}>
                <Heading as="h2" mb="4" size="4" style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--space-6)',
              height: 'var(--space-6)',
              userSelect: 'none',
              borderRadius: '100%',
              backgroundColor: 'var(--gray-a4)'
            }}>
                  {group.letter.toUpperCase()}
                </Heading>
                <Grid data-algolia-exclude columns={{
              sm: '2',
              md: '3'
            }} gap="4" mb="8">
                  {group.integrations.map(({
                url,
                badge,
                description,
                title,
                icon
              }) => <Card key={url} asChild size={{
                initial: '3',
                md: '4'
              }} variant="classic">
                        <NextLink href={url}>
                          <Flex align="center" gap="4" justify="between" my="-1">
                            <Box>
                              <Flex align="center" gap="2" mb="1">
                                <Heading as="h3" size="3" trim="start">
                                  {title}
                                </Heading>
                                {badge && <Badge color="purple">{badge}</Badge>}
                              </Flex>
                              <Text as="div" color="gray" size="2" trim="end" wrap="balance">
                                {description}
                              </Text>
                            </Box>

                            {icon && <ProviderIcon mr="-2" provider={icon} />}
                          </Flex>
                        </NextLink>
                      </Card>)}
                </Grid>
              </Box>)}
          </Box>
        </PageLayout.Content>
      </PageLayout.Root>
    </Box>;
};
export default IntegrationsPage;